package com.yendo.mx.ui.search

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Fastfood
import androidx.compose.material.icons.filled.History
import androidx.compose.material.icons.filled.Store
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.input.TextFieldValue
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import androidx.lifecycle.viewmodel.compose.viewModel
import coil3.compose.AsyncImage
import com.yendo.mx.ui.components.BuscadorYendo
import com.yendo.mx.ui.theme.RosaYendo
import com.yendo.mx.utils.ConfigManager
import com.yendo.mx.utils.EstadoCarga
import com.yendo.mx.ui.home.ChipFiltro
import com.yendo.mx.repository.RestauranteRepository
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext
import kotlinx.coroutines.Dispatchers

// 🚀 MODELO TEMPORAL DE RESULTADOS
data class ResultadoBusqueda(
    val id: String,
    val titulo: String,
    val subtitulo: String,
    val imagenUrl: String,
    val tipo: String, // "NEGOCIO" o "PRODUCTO"
    val idRestaurante: String = "" // 🚀 NUEVO: Para saber a quién pertenece el platillo
)

// 🚀 VIEWMODEL
@OptIn(FlowPreview::class)
class BuscadorViewModel : ViewModel() {
    private val _textoBusqueda = MutableStateFlow(TextFieldValue("")) // 🚀 UX: TextFieldValue para control de cursor
    val textoBusqueda: StateFlow<TextFieldValue> = _textoBusqueda.asStateFlow()

    private val _resultados = MutableStateFlow<List<ResultadoBusqueda>>(emptyList())
    val resultados: StateFlow<List<ResultadoBusqueda>> = _resultados.asStateFlow()

    private val _isSearching = MutableStateFlow(false)
    val isSearching: StateFlow<Boolean> = _isSearching.asStateFlow()

    // 🚀 PAGINACIÓN Y CONTROL DE CARGA
    private var paginaActual = 0
    private var isLastPage = false
    private var idZonaActual: String = ""

    // 🚀 HISTORIAL: Lista de búsquedas recientes
    private val _busquedasRecientes = MutableStateFlow<List<String>>(emptyList())
    val busquedasRecientes: StateFlow<List<String>> = _busquedasRecientes.asStateFlow()

    init {
        viewModelScope.launch {
            _textoBusqueda
                .map { it.text }
                .distinctUntilChanged()
                .debounce(400) // 🚀 DEBOUNCE: Ahorro de API y Batería
                .collectLatest { query ->
                    if (query.trim().isNotEmpty()) {
                        reiniciarYBuscar(query.trim())
                    } else {
                        _resultados.value = emptyList()
                        _isSearching.value = false
                    }
                }
        }
    }

    var tipoNegocioFiltro: String = ""

    fun setZona(idZona: String, tipoNegocio: String = "") {
        idZonaActual = idZona
        tipoNegocioFiltro = tipoNegocio
    }

    private suspend fun reiniciarYBuscar(query: String) {
        paginaActual = 0
        isLastPage = false
        _isSearching.value = true

        withContext(Dispatchers.Default) {
            try {
                val hits = com.yendo.mx.repository.AlgoliaRepository.buscarEnAlgolia(
                    query = query,
                    idZona = idZonaActual,
                    tipoNegocio = tipoNegocioFiltro,
                    page = paginaActual
                )
                println("YENDO_LOG ALGOLIA: Viewmodel recibió ${hits.size} hits (Pag $paginaActual)")
                _resultados.value = hits
                if (hits.isEmpty()) isLastPage = true
            } catch (e: Exception) {
                println("YENDO_LOG ALGOLIA: 🚨 ERROR EN VIEWMODEL: ${e.message}")
                _resultados.value = emptyList()
            } finally {
                _isSearching.value = false
            }
        }
    }

    fun cargarSiguientePagina() {
        val query = _textoBusqueda.value.text.trim()
        if (query.isEmpty() || _isSearching.value || isLastPage) return

        viewModelScope.launch {
            _isSearching.value = true
            paginaActual++

            withContext(Dispatchers.Default) {
                try {
                    val nuevosHits = com.yendo.mx.repository.AlgoliaRepository.buscarEnAlgolia(
                        query = query,
                        idZona = idZonaActual,
                        page = paginaActual
                    )
                    
                    if (nuevosHits.isEmpty()) {
                        isLastPage = true
                    } else {
                        // 🚀 ANEXAMOS RESULTADOS (Paginación Infinita)
                        _resultados.value = _resultados.value + nuevosHits
                    }
                    println("YENDO_LOG ALGOLIA: Paginación cargada. Total ahora: ${_resultados.value.size}")
                } catch (e: Exception) {
                    println("YENDO_LOG ALGOLIA: 🚨 Error en paginación: ${e.message}")
                } finally {
                    _isSearching.value = false
                }
            }
        }
    }

    fun actualizarBusqueda(nuevoValor: TextFieldValue) {
        _textoBusqueda.value = nuevoValor
    }

    // 🚀 UX: Inyectar texto desde cápsula y poner cursor al final
    fun inyectarBusquedaDesdeCapsula(texto: String) {
        _textoBusqueda.value = TextFieldValue(
            text = texto,
            selection = TextRange(texto.length)
        )
        agregarBusquedaReciente(texto)
    }

    // 🚀 HISTORIAL: Guardar búsqueda sin duplicados
    fun agregarBusquedaReciente(query: String) {
        if (query.isBlank()) return
        val listaActual = _busquedasRecientes.value.toMutableList()
        listaActual.remove(query)
        listaActual.add(0, query)
        _busquedasRecientes.value = listaActual.take(8) // Mantener top 8
    }

    fun limpiarHistorial() {
        _busquedasRecientes.value = emptyList()
    }
}

// 🚀 LA INTERFAZ PRINCIPAL ACTUALIZADA
@OptIn(ExperimentalLayoutApi::class)
@Composable
fun BuscadorAlgoliaScreen(
    viewModel: BuscadorViewModel = viewModel { BuscadorViewModel() },
    focoAutomatico: Boolean = true,
    tipoNegocioFiltro: String = "", // 🚀 "RESTAURANTE" O "COMERCIO" O VACÍO PARA BÚSQUEDA GLOBAL
    onNegocioClick: (String) -> Unit = {},
    onProductoClick: (String, String) -> Unit = { _, _ -> },
    onBack: () -> Unit = {} // 🚀 BOTÓN Y ACCIÓN PARA REGRESAR AL HOME
) {
    val textoBusqueda by viewModel.textoBusqueda.collectAsState()
    val resultados by viewModel.resultados.collectAsState()
    val isSearching by viewModel.isSearching.collectAsState()
    val busquedasRecientes by viewModel.busquedasRecientes.collectAsState()
    val focusManager = LocalFocusManager.current

    val isDark = isSystemInDarkTheme()
    val colorTextoPrincipal = if (isDark) Color.White else Color.Black
    val colorTextoSec = if (isDark) Color.LightGray else Color.DarkGray

    val restaurantes by RestauranteRepository.obtenerRestaurantes().collectAsState(initial = emptyList())
    val estadoConfig by ConfigManager.estadoConfig.collectAsState()
    val idZonaActual = (estadoConfig as? EstadoCarga.Exito)?.id_zona

    // 🚀 SINCRONIZACIÓN DE ZONA Y FILTRO DE NEGOCIO
    LaunchedEffect(idZonaActual, tipoNegocioFiltro) {
        if (idZonaActual != null) viewModel.setZona(idZonaActual, tipoNegocioFiltro)
    }

    val capsules = remember(idZonaActual, restaurantes, estadoConfig) {
        val config = ConfigManager.configActiva
        val lista = mutableListOf<ChipFiltro>()
        
        if (idZonaActual != null) {
            val subcatsVivas = mutableSetOf<String>()
            
            restaurantes.filter { it.ubicacion.idZona == idZonaActual && it.operacion.visible && it.operacion.activoComercio }
                .forEach { rest ->
                    rest.infoPublica.subcategorias.forEach { sub ->
                        if (sub.isNotBlank()) subcatsVivas.add(sub.trim())
                    }
                }

            val catalogoRest = config?.clasificacion?.restaurantes ?: emptyList()
            val catalogoCom = config?.clasificacion?.comercios ?: emptyList()
            
            // 🚀 ORDENACIÓN POR CATÁLOGO MAESTRO (BÚSQUEDA)
            (catalogoRest + catalogoCom).filter { it.activa }.forEach { cat ->
                if (lista.none { it.nombre.equals(cat.nombre, ignoreCase = true) }) {
                    lista.add(ChipFiltro(cat.nombre, cat.emoji))
                }
            }

            // 🚀 AGREGAMOS SUBCATEGORÍAS VIVAS QUE NO ESTÉN EN EL CATÁLOGO
            subcatsVivas.forEach { sub ->
                if (lista.none { it.nombre.equals(sub, ignoreCase = true) }) {
                    lista.add(ChipFiltro(sub, ""))
                }
            }
        }
        
        lista // 🚀 YA NO ORDENAMOS ALFABÉTICAMENTE, respetamos el orden del catálogo
    }

    Column(modifier = Modifier.fillMaxSize()) {
        // 1. Barra de Búsqueda
        BuscadorYendo(
            valor = textoBusqueda,
            alCambiar = { viewModel.actualizarBusqueda(it) },
            focoAutomatico = focoAutomatico,
            alBuscar = { viewModel.agregarBusquedaReciente(textoBusqueda.text) },
            onBack = onBack
        )

        // 🚀 1.5 Cápsulas de Sugerencia (Vista de Cascada/Grid)
        if (textoBusqueda.text.isEmpty()) {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .verticalScroll(rememberScrollState())
                    .padding(16.dp)
            ) {
                // 🚀 SECCIÓN: BÚSQUEDAS RECIENTES
                if (busquedasRecientes.isNotEmpty()) {
                    Row(
                        modifier = Modifier.fillMaxWidth().padding(bottom = 12.dp),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(
                            text = "Búsquedas recientes",
                            fontWeight = FontWeight.Bold,
                            fontSize = 14.sp,
                            color = colorTextoSec
                        )
                        Text(
                            text = "Limpiar",
                            fontSize = 12.sp,
                            color = RosaYendo,
                            modifier = Modifier.clickable { viewModel.limpiarHistorial() }
                        )
                    }

                    FlowRow(
                        modifier = Modifier.fillMaxWidth().padding(bottom = 24.dp),
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        busquedasRecientes.forEach { query ->
                            RecentSearchCapsule(
                                text = query,
                                isDark = isDark,
                                onClick = { 
                                    viewModel.inyectarBusquedaDesdeCapsula(query)
                                    focusManager.clearFocus() // 🚀 UX: Ocultar teclado
                                }
                            )
                        }
                    }
                }

                Text(
                    text = "Explora por categorías",
                    fontWeight = FontWeight.ExtraBold,
                    fontSize = 18.sp,
                    color = colorTextoPrincipal,
                    modifier = Modifier.padding(bottom = 16.dp)
                )

                FlowRow(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    capsules.forEach { chip ->
                        CategoryCapsule(
                            chip = chip,
                            isDark = isDark,
                            onClick = { 
                                viewModel.inyectarBusquedaDesdeCapsula(chip.nombre)
                                focusManager.clearFocus() // 🚀 UX: Ocultar teclado
                            }
                        )
                    }
                }
            }
        }

        // 2. Estado de Carga
        if (isSearching) {
            Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = RosaYendo)
            }
        }
        // 3. Estado Vacío (Sin resultados)
        else if (textoBusqueda.text.isNotEmpty() && resultados.isEmpty()) {
            Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                Text("No encontramos resultados para '${textoBusqueda.text}'", color = colorTextoSec)
            }
        }
        // 4. Lista de Resultados Categorizada
        else if (textoBusqueda.text.isNotEmpty()) {
            // Guardar en el historial cuando hay resultados y se interactúa (Enter se maneja en el componente)
            
            val restaurantesHits = resultados.filter { it.tipo == "NEGOCIO" }
            val productos = resultados.filter { it.tipo == "PRODUCTO" }

            val listState = rememberLazyListState()

            // 🚀 DETECCIÓN DE FINAL DE LISTA (Infinite Scroll)
            LaunchedEffect(listState) {
                snapshotFlow { listState.layoutInfo.visibleItemsInfo }
                    .mapNotNull { it.lastOrNull()?.index }
                    .collect { lastIdx ->
                        if (lastIdx >= resultados.size - 5) {
                            viewModel.cargarSiguientePagina()
                        }
                    }
            }

            LazyColumn(
                state = listState,
                modifier = Modifier.fillMaxSize(),
                contentPadding = PaddingValues(16.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                // 🏪 SECCIÓN DE RESTAURANTES
                if (restaurantesHits.isNotEmpty()) {
                    item {
                        Text(
                            text = "Restaurantes",
                            fontWeight = FontWeight.ExtraBold,
                            fontSize = 18.sp,
                            color = colorTextoPrincipal,
                            modifier = Modifier.padding(bottom = 4.dp, top = 8.dp)
                        )
                    }
                    items(restaurantesHits, key = { it.id }) { hit ->
                        FilaResultadoAlgolia(hit, colorTextoPrincipal, colorTextoSec) {
                            viewModel.agregarBusquedaReciente(textoBusqueda.text) // 🚀 UX: Guardar al hacer clic
                            onNegocioClick(hit.id)
                        }
                    }
                }

                // 🍔 SECCIÓN DE PRODUCTOS
                if (productos.isNotEmpty()) {
                    item {
                        Text(
                            text = "Platillos y Productos",
                            fontWeight = FontWeight.ExtraBold,
                            fontSize = 18.sp,
                            color = colorTextoPrincipal,
                            modifier = Modifier.padding(bottom = 4.dp, top = if (restaurantesHits.isNotEmpty()) 16.dp else 8.dp)
                        )
                    }
                    items(productos, key = { it.id }) { hit ->
                        FilaResultadoAlgolia(hit, colorTextoPrincipal, colorTextoSec) {
                            viewModel.agregarBusquedaReciente(textoBusqueda.text) // 🚀 UX: Guardar al hacer clic
                            onProductoClick(hit.id, hit.idRestaurante)
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun CategoryCapsule(
    chip: ChipFiltro,
    isDark: Boolean,
    onClick: () -> Unit
) {
    val colorBg = if (isDark) Color(0xFF2C2C2E) else Color(0xFFF0F0F0)
    val colorText = if (isDark) Color.White else Color.Black

    Surface(
        onClick = onClick,
        color = colorBg,
        shape = RoundedCornerShape(20.dp)
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 16.dp, vertical = 10.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (chip.emoji.isNotEmpty()) {
                Text(text = chip.emoji, fontSize = 14.sp)
                Spacer(modifier = Modifier.width(6.dp))
            }
            Text(
                text = chip.nombre,
                color = colorText,
                fontWeight = FontWeight.Bold,
                fontSize = 13.sp
            )
        }
    }
}

// 🚀 COMPONENTE: Cápsula de Historial
@Composable
fun RecentSearchCapsule(
    text: String,
    isDark: Boolean,
    onClick: () -> Unit
) {
    val colorBg = if (isDark) Color(0xFF1C1C1E) else Color(0xFFF5F5F5)
    val colorText = if (isDark) Color.LightGray else Color.Gray

    Surface(
        onClick = onClick,
        color = colorBg,
        shape = RoundedCornerShape(20.dp),
        border = androidx.compose.foundation.BorderStroke(1.dp, colorText.copy(alpha = 0.2f))
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 14.dp, vertical = 8.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Icon(Icons.Default.History, null, tint = colorText, modifier = Modifier.size(14.dp))
            Spacer(modifier = Modifier.width(6.dp))
            Text(
                text = text,
                color = colorText,
                fontWeight = FontWeight.Medium,
                fontSize = 13.sp
            )
        }
    }
}

// 🚀 COMPONENTE REUTILIZABLE PARA LAS FILAS
@Composable
fun FilaResultadoAlgolia(
    hit: ResultadoBusqueda,
    colorTextoPrincipal: Color,
    colorTextoSec: Color,
    onClick: () -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .clickable { onClick() }
            .padding(8.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        if (hit.imagenUrl.isNotBlank()) {
            AsyncImage(
                model = hit.imagenUrl,
                contentDescription = hit.titulo,
                contentScale = ContentScale.Crop,
                modifier = Modifier
                    .size(56.dp)
                    .clip(RoundedCornerShape(10.dp))
                    .background(Color.Gray.copy(alpha = 0.2f))
            )
        } else {
            val icono = if (hit.tipo == "NEGOCIO") Icons.Default.Store else Icons.Default.Fastfood
            Box(
                modifier = Modifier
                    .size(56.dp)
                    .background(Color.Gray.copy(alpha = 0.2f), RoundedCornerShape(10.dp)),
                contentAlignment = Alignment.Center
            ) {
                Icon(icono, contentDescription = null, tint = RosaYendo)
            }
        }

        Spacer(modifier = Modifier.width(16.dp))

        Column(modifier = Modifier.weight(1f)) {
            Text(text = hit.titulo, fontWeight = FontWeight.Bold, fontSize = 16.sp, color = colorTextoPrincipal)
            Spacer(modifier = Modifier.height(2.dp))
            Text(text = hit.subtitulo, color = colorTextoSec, fontSize = 13.sp, maxLines = 2)
        }

        // 🚀 INYECCIÓN: Botón de Favorito en Buscador
        com.yendo.mx.ui.components.BotonFavorito(
            id = hit.id,
            tipo = if (hit.tipo == "NEGOCIO") "RESTAURANTE" else "PRODUCTO",
            idRestauranteAsociado = hit.idRestaurante,
            size = 36.dp,
            iconSize = 20.dp
        )
    }
}
